import { Component, Inject, Input, OnInit } from '@angular/core';
import {
  NAVIGATION_ITEM_FLAT_TREE_CONFIG,
  NavigationItemFlatTreeConfig
} from '@/app/platform/platform-shared/navigation-item-tree/types';

import { NavigationItemAuthorizationSelectionForm } from '@/app/platform/platform-management/user-group-block/containers/user-group-linked-app-authorizations-editor/navigation-item-authorization-selection-form';
import { FeatureAuthorizationSelectionForm } from '@/app/platform/platform-management/user-group-block/containers/user-group-linked-app-authorizations-editor/feature-authorization-selection-form';
import { SelectionChangeType } from '@/app/platform/platform-management/user-group-block/selection-form';
import { NavigationItemTreeComponent } from '@/app/platform/platform-shared/navigation-item-tree/navigation-item-tree-component';
import { NavigationItemEntity } from '@/app/platform/platform-data/models/navigation-item.entity';
import {
  AUTHORIZATION_ACTIONS,
  AuthorizationActions,
  AuthorizationCommandType,
  ChangeAuthorizationCommand,
  USER_GROUP_ACTIONS,
  UserGroupActions
} from '@/app/platform/platform-data/actions/types';
import { EgiAppEntity } from '@/app/platform/platform-data/models/egi-app.entity';
import { UserGroupEntity } from '@/app/platform/platform-data/models/user-group.entity';
import { PageFeature } from '@ervo/page-framework';

@Component({
    selector: 'user-group-linked-app-authorizations-editor[userGroup][app]',
    templateUrl: './user-group-linked-app-authorizations-editor.component.html',
    styleUrls: ['./user-group-linked-app-authorizations-editor.component.css'],
    providers: [NavigationItemAuthorizationSelectionForm, FeatureAuthorizationSelectionForm],
    standalone: false
})
export class UserGroupLinkedAppAuthorizationsEditorComponent
  extends NavigationItemTreeComponent<NavigationItemEntity, EgiAppEntity>
  implements OnInit
{
  @Input('userGroup') set userGroupInput(v: UserGroupEntity) {
    this.userGroup = v;
    this.initForms();
    this.onAppChange();
  }

  userGroup: UserGroupEntity;

  constructor(
    @Inject(NAVIGATION_ITEM_FLAT_TREE_CONFIG)
    public readonly navigationItemFlatTreeConfig: NavigationItemFlatTreeConfig<NavigationItemEntity>,
    @Inject(USER_GROUP_ACTIONS)
    private readonly userGroupActions: UserGroupActions,
    @Inject(AUTHORIZATION_ACTIONS)
    private readonly authorizationActions: AuthorizationActions,
    readonly navigationItemsSelectionForm: NavigationItemAuthorizationSelectionForm,
    readonly featureAuthorizationSelectionForm: FeatureAuthorizationSelectionForm
  ) {
    super(
      navigationItemFlatTreeConfig,
      node => node.id + this.userGroup?.id,
      () => this.initForms(),
      () => !!this.userGroup
    );
  }

  saving = false;

  get dirty(): boolean {
    return (
      this.navigationItemsSelectionForm.isDirty() ||
      this.featureAuthorizationSelectionForm.isDirty()
    );
  }

  get allActionsShouldBeBlocked(): boolean {
    return this.app.shouldBeSynced;
  }

  ngOnInit() {}

  initForms(): void {
    if (this.app && this.userGroup && !this.saving) {
      this.navigationItemsSelectionForm.init(this.app, this.userGroup);
      this.featureAuthorizationSelectionForm.init(this.app, this.userGroup);
    }
  }

  selectAll() {
    this.navigationItemsSelectionForm.selectAll();
    this.featureAuthorizationSelectionForm.selectAll();
  }

  // Zonder trackBy hertekent Angular de togglers bij elke change detection (nieuwe
  // objectreferenties), waardoor een klik tussen mousedown en mouseup verloren kan gaan.
  trackByFeatureId(_index: number, feature: PageFeature): string {
    return feature.featureId;
  }

  toggleFeature(feature: PageFeature, item: NavigationItemEntity): void {
    const isNowSelected = this.featureAuthorizationSelectionForm.toggle(feature, item);

    if (isNowSelected) {
      this.navigationItemsSelectionForm.select(item);
    }
  }

  toggleNavigationItem(item: NavigationItemEntity, event: Event): void {
    event.stopPropagation();
    const isNowSelected = this.navigationItemsSelectionForm.toggle(item);
    if (!isNowSelected) {
      this.deselectAllFeaturesForNavigationItemAndChildren(item);
    }
  }

  async onSave() {
    this.saving = true;
    const navigationItemCommands = this.makeNavigationItemAuthorizationCommands();
    const featureCommands = this.makeFeatureAuthorizationCommands();

    const result = await this.authorizationActions.changeAuthorizations(
      this.userGroup,
      navigationItemCommands,
      featureCommands
    );

    this.saving = false;
  }

  onUndoAll(): void {
    // TODO: do this without reinitializing the forms
    this.initForms();
  }

  private makeNavigationItemAuthorizationCommands(): ChangeAuthorizationCommand<NavigationItemEntity>[] {
    const changes = this.navigationItemsSelectionForm.getChanges();

    return changes.map(change => {
      const type = this.convertSelectionChangeTypeToAuthorizationActionType(change.type);
      return {
        type,
        item: change.item
      };
    });
  }

  private makeFeatureAuthorizationCommands(): ChangeAuthorizationCommand<
    PageFeature,
    NavigationItemEntity
  >[] {
    const changes = this.featureAuthorizationSelectionForm.getChanges();
    return changes.map(({ item, type }) => ({
      extraItem: item.navigationItem,
      item: item.feature,
      type: this.convertSelectionChangeTypeToAuthorizationActionType(type)
    }));
  }

  private convertSelectionChangeTypeToAuthorizationActionType(
    type: SelectionChangeType
  ): AuthorizationCommandType {
    if (type === 'None') {
      throw new Error('Cannot convert None to authorize or unAuthorize');
    }

    return type === 'Select' ? 'authorize' : 'unAuthorize';
  }

  private deselectAllFeaturesForNavigationItemAndChildren(item: NavigationItemEntity): void {
    const affectedItems = item.asArray;

    affectedItems.forEach(i => {
      i.regularFeatures.forEach(f => this.featureAuthorizationSelectionForm.deselect(f, i));
    });
  }
}
